import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import restaurantRobotImg from "@/assets/blog/restaurant-service-robot.jpg";
import voiceAIImg from "@/assets/blog/voice-ai-automation.jpg";
import hotelCleaningImg from "@/assets/blog/hotel-cleaning-robot.jpg";
import warehouseAutomationImg from "@/assets/blog/warehouse-automation.jpg";
import healthcareRobotImg from "@/assets/blog/healthcare-delivery-robot.jpg";
import businessROIImg from "@/assets/blog/business-automation-roi.jpg";

const blogPosts = [
  {
    id: 1,
    title: "How PUDU Service Robots Are Transforming Restaurant Operations",
    excerpt: "Discover how BellaBot and KettyBot Pro are revolutionizing food service by reducing labor costs up to 40%, improving table turnover by 30%, and enhancing customer experiences in restaurants worldwide.",
    image: restaurantRobotImg,
    date: "2025-01-15",
    readTime: "6 min read",
    category: "Restaurants",
    slug: "pudu-robots-transforming-restaurants"
  },
  {
    id: 2,
    title: "Voice AI: Your 24/7 Business Development Representative",
    excerpt: "Learn how Neil AI's voice automation handles customer inquiries, qualifies leads, and books appointments around the clock—reducing response times by 95% while your team focuses on closing deals.",
    image: voiceAIImg,
    date: "2025-01-10",
    readTime: "7 min read",
    category: "AI Technology",
    slug: "voice-ai-business-automation"
  },
  {
    id: 3,
    title: "ROI Analysis: Why Hotels Choose CC1 Pro & MT1 Cleaning Robots",
    excerpt: "Real numbers from hospitality leaders: 60% reduction in cleaning costs, 99.9% floor coverage, and 24/7 operation. See why Marriott and Sheraton trust PUDU cleaning solutions.",
    image: hotelCleaningImg,
    date: "2025-01-05",
    readTime: "8 min read",
    category: "Hotels",
    slug: "hotel-cleaning-robots-roi-analysis"
  },
  {
    id: 4,
    title: "Warehouse Automation: The T600 & FlashBot Max Advantage",
    excerpt: "From Amazon to QuikTrip, discover how autonomous delivery robots are revolutionizing logistics with 500kg payload capacity, 99.8% navigation accuracy, and seamless WMS integration.",
    image: warehouseAutomationImg,
    date: "2024-12-28",
    readTime: "9 min read",
    category: "Warehouses",
    slug: "warehouse-robots-logistics-automation"
  },
  {
    id: 5,
    title: "Autonomous Delivery Robots: Transforming Healthcare Efficiency",
    excerpt: "Hospital case studies reveal 70% reduction in staff delivery time, improved infection control, and 24/7 medication transport with HolaBot and PUDU D9 healthcare solutions.",
    image: healthcareRobotImg,
    date: "2024-12-20",
    readTime: "7 min read",
    category: "Healthcare",
    slug: "healthcare-delivery-robots-case-studies"
  },
  {
    id: 6,
    title: "Complete ROI Calculator: AI & Robotics Investment Guide",
    excerpt: "Calculate your exact savings with our interactive ROI tool. Most businesses see full payback in 6-12 months with 300%+ ROI over 3 years. See your personalized business case today.",
    image: businessROIImg,
    date: "2024-12-15",
    readTime: "10 min read",
    category: "Business Strategy",
    slug: "ai-robotics-roi-calculator-guide"
  }
];

const Blog = () => {
  return (
    <>
      <SEO 
        title="AI & Robotics Blog | Neil AI Global Solutions"
        description="Stay updated with the latest insights on AI automation, service robots, voice AI, and business technology. Expert guides, case studies, and industry trends."
        keywords="AI blog, robotics news, automation insights, business technology, voice AI, service robots, CRM integration, digital transformation"
      />
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: "hsl(var(--background))" }}>
        <Navigation />
        
        <main className="flex-grow container mx-auto px-4 py-24">
          {/* Hero Section */}
          <div className="text-center mb-16 animate-fade-in">
            <h1 className="font-orbitron font-bold text-5xl md:text-6xl mb-6">
              Neil AI <span className="text-neon-teal">Blog</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Insights on AI automation, robotics, and digital transformation for modern businesses
            </p>
          </div>

          {/* Featured Post */}
          <Card className="mb-16 overflow-hidden glass-effect border-[hsl(var(--line))] hover:border-primary/40 transition-all duration-300">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/5 flex items-center justify-center min-h-[300px]">
                <img 
                  src={blogPosts[0].image} 
                  alt={blogPosts[0].title}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <span className="text-primary text-sm font-bold mb-2">{blogPosts[0].category}</span>
                <h2 className="font-orbitron font-bold text-3xl mb-4">{blogPosts[0].title}</h2>
                <p className="text-muted-foreground mb-4">{blogPosts[0].excerpt}</p>
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {new Date(blogPosts[0].date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {blogPosts[0].readTime}
                  </span>
                </div>
                <Button className="w-fit group">
                  Read More
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </Card>

          {/* Blog Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.slice(1).map((post) => (
              <Card 
                key={post.id}
                className="glass-effect border-[hsl(var(--line))] hover:border-primary/40 transition-all duration-300 hover:scale-105 overflow-hidden group cursor-pointer"
              >
                <div className="bg-white/5 h-48 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    loading="lazy"
                    width="600"
                    height="400"
                  />
                </div>
                <div className="p-6">
                  <span className="text-primary text-xs font-bold mb-2 block">{post.category}</span>
                  <h3 className="font-orbitron font-bold text-lg mb-3 line-clamp-2">{post.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-3">{post.excerpt}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mb-4">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {post.readTime}
                    </span>
                  </div>
                  <Button variant="ghost" size="sm" className="w-full group">
                    Read Article
                    <ArrowRight className="ml-2 w-3 h-3 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>

          {/* Newsletter Signup */}
          <Card className="mt-16 p-8 text-center glass-effect border-[hsl(var(--line))]">
            <h2 className="font-orbitron font-bold text-3xl mb-4">Stay Updated</h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Get the latest insights on AI automation, robotics, and business technology delivered to your inbox.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input 
                type="email" 
                placeholder="Enter your email"
                className="flex-grow px-4 py-3 rounded-lg bg-white/5 border border-[hsl(var(--line))] focus:border-primary outline-none"
              />
              <Button className="bg-primary hover:bg-primary/90">
                Subscribe
              </Button>
            </div>
          </Card>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Blog;
